import { Component } from '@theme/component';

const VALID_VALUES = ['him', 'her', 'unisex'];

/**
 * Client-side gender filter for the Fragrances product grid.
 *
 * Filters existing `[data-gender]` grid items in place (no re-fetch), keeps
 * a row of plain buttons in sync via `aria-pressed`, and mirrors the current
 * value to `?for=` in the URL without adding a history entry.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} button
 *
 * @extends {Component<Refs>}
 */
export class GenderFilterComponent extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();

    const requested = new URLSearchParams(window.location.search).get('for');
    const initial = VALID_VALUES.includes(requested ?? '') ? /** @type {string} */ (requested) : 'all';

    this.#apply(initial, { updateUrl: false });
  }

  /**
   * Bound to each button via `on:click="/setFilter?value=..."`.
   * @param {{ value: string }} data
   */
  setFilter(data) {
    this.#apply(data.value, { updateUrl: true });
  }

  /**
   * @param {string} value
   * @param {{ updateUrl: boolean }} options
   */
  #apply(value, { updateUrl }) {
    for (const button of this.refs.button) {
      button.setAttribute('aria-pressed', String(button.dataset.value === value));
    }

    for (const card of document.querySelectorAll('[data-gender]')) {
      if (!(card instanceof HTMLElement)) continue;

      const gender = card.dataset.gender;
      let visible = true;

      if (value === 'him') visible = gender === 'him' || gender === 'unisex';
      else if (value === 'her') visible = gender === 'her' || gender === 'unisex';
      else if (value === 'unisex') visible = gender === 'unisex';

      card.hidden = !visible;
    }

    if (!updateUrl) return;

    const url = new URL(window.location.href);
    if (value === 'all') {
      url.searchParams.delete('for');
    } else {
      url.searchParams.set('for', value);
    }
    history.replaceState(history.state, '', url);
  }
}

if (!customElements.get('gender-filter-component')) {
  customElements.define('gender-filter-component', GenderFilterComponent);
}
